import type { KlipyGif, KlipyGifsPageResult } from '../../types';
import { useNetworkStatus } from 'Hooks/network';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useUIStore } from '~/modules/ui/index.store';
import {
  KLIPY_SEARCH_MAX_GIFS,
  KLIPY_TRENDING_PER_PAGE,
  SEARCH_DEBOUNCE_MS,
} from '../../constants';
import { loadKlipyGifsPage } from '../../requests';

export function useGifPicker(klipyApiKey: string): {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  submitSearch: () => void;
  gifs: KlipyGif[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMoreSentinelRef: RefObject<HTMLDivElement | null>;
  resetGifPickerState: () => void;
  showLoadMoreSentinel: boolean;
  showNoResultsEmptyState: boolean;
  showErrorEmptyState: boolean;
} {
  const gifPicker = useUIStore((s) => s.gifPicker);
  const setGifPicker = useUIStore((s) => s.setGifPicker);
  const resetGifPicker = useUIStore((s) => s.resetGifPicker);
  const { isOnline } = useNetworkStatus();

  const abortRef = useRef<AbortController | null>(null);
  const loadMoreAbortRef = useRef<AbortController | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const searchLoadMoreBusyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [submitVersion, setSubmitVersion] = useState(0);

  const resetGifPickerState = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    abortRef.current?.abort();
    loadMoreAbortRef.current?.abort();
    searchLoadMoreBusyRef.current = false;
    resetGifPicker();
  }, [resetGifPicker]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setGifPicker({ debouncedQuery: gifPicker.searchQuery.trim() });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [gifPicker.searchQuery, setGifPicker]);

  const loadMoreSearchGifs = useCallback(async () => {
    const state = useUIStore.getState().gifPicker;

    if (!klipyApiKey) {
      return;
    }
    if (state.debouncedQuery.length === 0) {
      return;
    }
    if (!state.searchHasMore || state.gifs.length >= KLIPY_SEARCH_MAX_GIFS) {
      return;
    }
    if (state.loading || state.loadingMore) {
      return;
    }
    if (searchLoadMoreBusyRef.current) {
      return;
    }
    searchLoadMoreBusyRef.current = true;

    loadMoreAbortRef.current?.abort();
    const controller = new AbortController();
    loadMoreAbortRef.current = controller;

    setGifPicker({ loadingMore: true });
    try {
      const pageResult: KlipyGifsPageResult = await loadKlipyGifsPage({
        apiKey: klipyApiKey,
        path: 'search',
        query: state.debouncedQuery,
        page: state.searchLastPage + 1,
        signal: controller.signal,
      });

      const { gifs: next, hasNext, currentPage } = pageResult;

      if (controller.signal.aborted) {
        return;
      }

      useUIStore.setState((s) => {
        const prev = s.gifPicker.gifs;
        const merged = [...prev, ...next].slice(0, KLIPY_SEARCH_MAX_GIFS);
        return {
          gifPicker: {
            ...s.gifPicker,
            gifs: merged,
            searchLastPage: currentPage,
            searchHasMore: hasNext && merged.length < KLIPY_SEARCH_MAX_GIFS,
          },
        };
      });
    } catch {
      if (controller.signal.aborted) {
        return;
      }
      setGifPicker({ searchHasMore: false });
    } finally {
      searchLoadMoreBusyRef.current = false;
      setGifPicker({ loadingMore: false });
    }
  }, [klipyApiKey, setGifPicker]);

  useEffect(() => {
    if (!gifPicker.isOpen) {
      return;
    }

    if (!klipyApiKey) {
      setGifPicker({
        gifs: [],
        loading: false,
        loadingMore: false,
        error: null,
        searchHasMore: false,
        searchLastPage: 0,
      });
      return;
    }

    abortRef.current?.abort();
    loadMoreAbortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const isSearch = gifPicker.debouncedQuery.length > 0;
    const currentError = useUIStore.getState().gifPicker.error;

    if (!isOnline) {
      // Offline — show error immediately, no fetch needed.
      setGifPicker({
        gifs: [],
        searchHasMore: false,
        searchLastPage: 0,
        loadingMore: false,
        loading: false,
        error: 'Could not load GIFs. Try again.',
      });
      return;
    }

    // Clear gifs before fetching. When retrying from an error state,
    // keep the existing error/loading values; otherwise reset them.
    setGifPicker({
      gifs: [],
      searchHasMore: false,
      searchLastPage: 0,
      loadingMore: false,
      ...(!currentError && { loading: true, error: null }),
    });

    void (async () => {
      try {
        const pageResult: KlipyGifsPageResult = await loadKlipyGifsPage({
          apiKey: klipyApiKey,
          path: isSearch ? 'search' : 'trending',
          query: gifPicker.debouncedQuery,
          page: 1,
          signal: controller.signal,
        });
        const { gifs: list, hasNext, currentPage } = pageResult;

        if (controller.signal.aborted) {
          return;
        }

        if (isSearch) {
          const nextList = list.slice(0, KLIPY_SEARCH_MAX_GIFS);
          setGifPicker({
            gifs: nextList,
            searchLastPage: currentPage,
            searchHasMore: hasNext && nextList.length < KLIPY_SEARCH_MAX_GIFS,
            error: null,
          });
        } else {
          setGifPicker({
            gifs: list.slice(0, KLIPY_TRENDING_PER_PAGE),
            error: null,
          });
        }
      } catch {
        if (controller.signal.aborted) {
          return;
        }

        setGifPicker({
          gifs: [],
          searchHasMore: false,
          error: 'Could not load GIFs. Try again.',
        });
      } finally {
        if (!controller.signal.aborted) {
          setGifPicker({ loading: false });
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    gifPicker.isOpen,
    gifPicker.debouncedQuery,
    klipyApiKey,
    setGifPicker,
    submitVersion,
    isOnline,
  ]);

  const isSearchMode = gifPicker.debouncedQuery.length > 0;
  const showLoadMoreSentinel =
    Boolean(klipyApiKey) &&
    isSearchMode &&
    gifPicker.searchHasMore &&
    gifPicker.gifs.length > 0 &&
    gifPicker.gifs.length < KLIPY_SEARCH_MAX_GIFS &&
    !gifPicker.loading &&
    !gifPicker.loadingMore;

  useEffect(() => {
    if (!gifPicker.isOpen || !showLoadMoreSentinel) {
      return;
    }

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) {
      return;
    }

    const root = sentinel.closest('[data-slot="scroll-area-viewport"]');

    if (!(root instanceof Element)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }
        void loadMoreSearchGifs();
      },
      { root, rootMargin: '80px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [
    gifPicker.isOpen,
    showLoadMoreSentinel,
    loadMoreSearchGifs,
    gifPicker.gifs.length,
  ]);

  const hasKey = Boolean(klipyApiKey);
  const idle = hasKey && !gifPicker.loading;
  const empty = gifPicker.gifs.length === 0;

  const showErrorEmptyState = idle && Boolean(gifPicker.error) && empty;
  const showNoResultsEmptyState = idle && !gifPicker.error && empty && gifPicker.debouncedQuery.length > 0;

  const setOpen = useCallback(
    (value: SetStateAction<boolean>) => {
      const current = useUIStore.getState().gifPicker.isOpen;
      const next = typeof value === 'function' ? value(current) : value;
      setGifPicker({ isOpen: next });
    },
    [setGifPicker]
  );

  const setSearchQuery = useCallback(
    (value: SetStateAction<string>) => {
      const current = useUIStore.getState().gifPicker.searchQuery;
      const next = typeof value === 'function' ? value(current) : value;
      setGifPicker({ searchQuery: next });
    },
    [setGifPicker]
  );

  const submitSearch = useCallback(() => {
    const current = useUIStore.getState().gifPicker.searchQuery;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    setGifPicker({ debouncedQuery: current.trim() });
    setSubmitVersion((version) => version + 1);
  }, [setGifPicker]);

  return {
    open: gifPicker.isOpen,
    setOpen,
    searchQuery: gifPicker.searchQuery,
    setSearchQuery,
    submitSearch,
    gifs: gifPicker.gifs,
    loading: gifPicker.loading,
    loadingMore: gifPicker.loadingMore,
    error: gifPicker.error,
    loadMoreSentinelRef,
    resetGifPickerState,
    showLoadMoreSentinel,
    showNoResultsEmptyState,
    showErrorEmptyState,
  };
}
